import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db, School } from '../../../utils/firebase';
import { collection, doc, getDoc, updateDoc, setDoc, onSnapshot, deleteDoc, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { Student, Teacher, isTeacher } from '../../../utils/firebase';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import CloseButton from '../../../components/ui/CloseButton';
import Dialog from '../../../components/ui/Dialog';




interface Role {
  id: string;
  name: string;
  description: string;
  salary: number;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  isPointManager: boolean;
  pointAutoPay: boolean;
  maxCount: number;
  docId?: string;
  minBidAmount: number; // 최소 입찰 금액
  maxRoleHolders: number; // 동시에 역할을 맡을 수 있는 최대 학생 수
  isAuctionable: boolean; // 경매 가능 여부
}

interface RoleAuction {
  id: string;
  roleId: string;
  roleName: string;
  roleDescription: string;
  currentBid: number;
  currentBidder: string | null;
  currentBidderName: string | null;
  minBid: number;
  startTime: Date;
  endTime: Date;
  status: 'pending' | 'active' | 'completed' | 'cancelled' | 'ready_to_assign';
  bidders: {
    studentId: string;
    studentName: string;
    bidAmount: number;
    bidTime: Date;
  }[];
  winner?: {
    studentId: string;
    studentName: string;
    bidAmount: number;
  };
  maxRoleHolders: number;
  participants: string[]; // 참여한 학생 ID 목록
  isAssigned: boolean; // 역할 할당 완료 여부
}

interface RoleAuctionSettings {
  enabled: boolean;
  auctionDuration: number; // 분 단위
  minBidIncrement: number; // 최소 입찰 증가액
  autoAssignOnEnd: boolean;
  defaultMaxRoleHolders: number;
  endCondition: 'time' | 'fixedTime' | 'manual'; // 종료 조건: 시간 기반, 지정 시간, 수동
  fixedEndTime?: Date; // 지정 시간 종료 시 사용
  isAnonymous: boolean; // 익명 경매 여부
}

const RoleAuctionManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [activeAuctions, setActiveAuctions] = useState<RoleAuction[]>([]);
  const [completedAuctions, setCompletedAuctions] = useState<RoleAuction[]>([]);
  const [auctionSettings, setAuctionSettings] = useState<RoleAuctionSettings>({
    enabled: false,
    auctionDuration: 30,
    minBidIncrement: 10,
    autoAssignOnEnd: true,
    defaultMaxRoleHolders: 1,
    endCondition: 'time',
    isAnonymous: false
  });
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    roleId: string;
  } | null>(null);
  
  // 정렬 상태
  const [sortBy, setSortBy] = useState<'name' | 'createdAt'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isMobile, setIsMobile] = useState(false);


  // 새 역할 폼 상태
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    salary: 0,
    salaryType: 'point' as 'point' | 'stamp',
    salaryPeriod: 'monthly' as 'daily' | 'weekly' | 'monthly',
    maxCount: 1,
    minBidAmount: 0,
    maxRoleHolders: 1,
    isPointManager: false,
    pointAutoPay: false,
    isAuctionable: true
  });

  // 역할 목록 가져오기
  useEffect(() => {
      if (!currentUser || !isTeacher(currentUser)) return;

    const loadRoles = async () => {
      try {
        const rolesRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/classRoles`);
        const rolesSnapshot = await getDocs(rolesRef);
        
        const rolesData = rolesSnapshot.docs.map(doc => {
          const data = doc.data();
          const role = {
            id: data.id || doc.id,
            docId: doc.id, // Firestore 문서 ID를 docId로 설정
            name: data.name || '',
            description: data.description || '',
            salary: data.salary || 0,
            salaryType: data.salaryType || 'point',
            salaryPeriod: data.salaryPeriod || 'monthly',
            isActive: data.isActive !== false,
            isPointManager: data.isPointManager || false,
            pointAutoPay: data.pointAutoPay || false,
            maxCount: data.maxCount || 1,
            minBidAmount: data.minBidAmount || 0,
            maxRoleHolders: data.maxRoleHolders || 1,
            isAuctionable: data.isAuctionable || false,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as Role;
          
          // 필수 필드 검증 및 디버깅
          if (!role.id || !role.docId || !role.name) {
            console.warn('Invalid role data found:', role);
            console.warn('Document ID:', doc.id);
            console.warn('Document data:', data);
            return null;
          }
          
          // docId가 제대로 설정되었는지 확인
          if (role.docId !== doc.id) {
            console.warn('docId mismatch:', { roleDocId: role.docId, actualDocId: doc.id });
            role.docId = doc.id; // 강제로 올바른 docId 설정
          }
          
          return role;
        }).filter(Boolean) as Role[];
        
        setRoles(rolesData.filter(role => role.isActive));
      } catch (err) {
        console.error('Error loading roles:', err);
        setError('역할 목록을 불러오는 중 오류가 발생했습니다.');
      }
    };

    loadRoles();
  }, [currentUser]);

  // 학생 목록 가져오기
  useEffect(() => {
      if (!currentUser || !isTeacher(currentUser)) return;

    const loadStudents = async () => {
      try {
        const studentsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students`);
        const studentsSnapshot = await getDocs(studentsRef);
        const studentsData = studentsSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        })) as Student[];
        setStudents(studentsData);
      } catch (err) {
        console.error('Error loading students:', err);
        setError('학생 목록을 불러오는 중 오류가 발생했습니다.');
      }
    };

    loadStudents();
  }, [currentUser]);

  // 활성 경매 모니터링
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;

    try {
    const auctionsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/roleAuctions`);
    const unsubscribe = onSnapshot(auctionsRef, (snapshot) => {
      const auctionsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          bidders: data.bidders?.map((bidder: any) => ({
            ...bidder,
            bidTime: bidder.bidTime?.toDate()
          })) || []
        } as RoleAuction;
      });
        
      setActiveAuctions(auctionsData.filter(auction => auction.status === 'active'));
        setCompletedAuctions(auctionsData.filter(auction => auction.status === 'completed'));
      }, (error) => {
        console.error('Error listening to auctions:', error);
        if (error.code === 'permission-denied') {
          setError('경매 데이터에 접근할 권한이 없습니다.');
        }
    });

    return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up auction listener:', err);
    }
  }, [currentUser]);

  // 경매 설정 로드
  useEffect(() => {
      if (!currentUser || !isTeacher(currentUser)) return;

    const loadAuctionSettings = async () => {
      try {
        // roleAuctionSettings를 서브컬렉션으로 저장
        const settingsRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/roleAuctionSettings`, 'settings');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          setAuctionSettings(settingsDoc.data() as RoleAuctionSettings);
        }
      } catch (err) {
        console.error('Error loading auction settings:', err);
      }
    };

    loadAuctionSettings();
  }, [currentUser]);

  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 정렬된 역할 목록 계산
  const sortedRoles = React.useMemo(() => {
    const sorted = [...roles].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name, 'ko');
      } else if (sortBy === 'createdAt') {
        comparison = a.createdAt.getTime() - b.createdAt.getTime();
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [roles, sortBy, sortOrder]);

  // 권한 확인
  if (!currentUser || !isTeacher(currentUser)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px', border: '1px solid #fcc' }}>
          교사 권한이 필요합니다. 로그인 상태를 확인해주세요.
        </div>
      </div>
    );
  }

  // 새 역할 생성
  const handleCreateRole = async () => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      const roleData = {
        ...newRole,
        id: Date.now().toString(),
        isActive: true,
        isAuctionable: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, roleData.id), roleData);
      
             // 로컬 상태 즉시 업데이트
       setRoles(prevRoles => [...prevRoles, roleData]);
       
       // 경매가 활성화되어 있고 새 역할이 경매 가능하다면 자동으로 경매 시작
       if (auctionSettings.enabled && roleData.isAuctionable) {
         await handleStartAuction(roleData);
         toast.success('새 역할이 생성되었고 경매가 자동으로 시작되었습니다.');
       } else {
         toast.success('새 역할이 생성되었습니다.');
       }
       
       setRoleDialogOpen(false);
       setNewRole({
         name: '',
         description: '',
         salary: 0,
         salaryType: 'point',
         salaryPeriod: 'monthly',
         maxCount: 1,
         minBidAmount: 0,
         maxRoleHolders: 1,
         isPointManager: false,
         pointAutoPay: false,
         isAuctionable: true
       });
    } catch (err) {
      console.error('Error creating role:', err);
      setError('역할 생성 중 오류가 발생했습니다.');
    }
  };

  // 역할 수정
  const handleUpdateRole = async () => {
    if (!editingRole) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      const updatedRole = {
        ...editingRole,
        name: newRole.name,
        description: newRole.description,
        salary: newRole.salary,
        salaryType: newRole.salaryType,
        salaryPeriod: newRole.salaryPeriod,
        maxCount: newRole.maxCount,
        minBidAmount: newRole.minBidAmount,
        maxRoleHolders: newRole.maxRoleHolders,
        isPointManager: newRole.isPointManager,
        pointAutoPay: newRole.pointAutoPay,
        isAuctionable: true, // 경매 관리 페이지에서 생성/수정하는 역할은 항상 경매 가능
        updatedAt: new Date()
      };

      await updateDoc(doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, editingRole.docId!), updatedRole);
      
      // 로컬 상태 즉시 업데이트
      setRoles(prevRoles => 
        prevRoles.map(role => 
          role.docId === editingRole.docId 
            ? { ...role, ...updatedRole }
            : role
        )
      );
      
      setRoleDialogOpen(false);
      setEditingRole(null);
      toast.success('역할이 수정되었습니다.');
    } catch (err) {
      console.error('Error updating role:', err);
      setError('역할 수정 중 오류가 발생했습니다.');
    }
  };

  // 역할 삭제
  const handleDeleteRole = async (docId: string) => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      await deleteDoc(doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, docId));
      
      // 로컬 상태 즉시 업데이트
      setRoles(prevRoles => prevRoles.filter(role => role.docId !== docId));
      
      toast.success('역할이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting role:', err);
      setError('역할 삭제 중 오류가 발생했습니다.');
    }
  };

  // 경매 가능 여부 토글
  const handleToggleAuctionable = async (role: Role) => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      // docId가 없으면 role.id를 사용하여 임시로 설정
      if (!role.docId) {
        console.warn('Role docId is missing, using role.id as fallback:', role);
        role.docId = role.id; // 임시로 id를 docId로 사용
      }

      const newAuctionable = !role.isAuctionable;
      const updatedRole = {
        ...role,
        isAuctionable: newAuctionable,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, role.docId), updatedRole);
      
      // 로컬 상태 즉시 업데이트
      setRoles(prevRoles => 
        prevRoles.map(r => 
          r.docId === role.docId 
            ? { ...r, isAuctionable: newAuctionable, updatedAt: new Date() }
            : r
        )
      );
      
      // 경매가 활성화되어 있고 역할이 경매 가능해졌다면 자동으로 경매 시작
      if (auctionSettings.enabled && newAuctionable) {
        await handleStartAuction(role); // 원본 role 객체 사용
        toast.success(`경매 가능으로 설정되었고 경매가 자동으로 시작되었습니다.`);
      } else if (auctionSettings.enabled && !newAuctionable) {
        // 경매가 활성화되어 있고 역할이 경매 불가능해졌다면 해당 경매 취소
        const activeAuction = activeAuctions.find(auction => auction.roleId === role.id);
        if (activeAuction) {
          await handleCancelAuction(activeAuction);
          toast.success(`경매 불가능으로 설정되었고 해당 경매가 취소되었습니다.`);
        } else {
          toast.success(`경매 불가능으로 설정되었습니다.`);
        }
      } else {
        toast.success(`경매 ${newAuctionable ? '가능' : '불가'}로 설정되었습니다.`);
      }
    } catch (err) {
      console.error('Error toggling auctionable:', err);
      setError('경매 가능 여부 변경 중 오류가 발생했습니다.');
    }
  };

    // 경매 결과 수동 반영
  const handleAssignRoleResult = async (auction: RoleAuction) => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      if (!auction.currentBidder) {
        toast.error('입찰자가 없어 역할을 할당할 수 없습니다.');
        return;
      }

      // 트랜잭션으로 경매 완료 및 포인트 환불 처리
      await runTransaction(db, async (transaction) => {
        const auctionRef = doc(db, `schools/${schoolId}/classes/${classId}/roleAuctions`, auction.id);
        
        // 입찰자들을 입찰 금액 순으로 정렬
        const sortedBidders = [...auction.bidders].sort((a, b) => b.bidAmount - a.bidAmount);
        
        // 동시 역할자 수만큼만 선별하고 나머지는 포인트 환불
        const selectedBidders = sortedBidders.slice(0, auction.maxRoleHolders);
        const refundedBidders = sortedBidders.slice(auction.maxRoleHolders);
        
        // 먼저 환불할 학생 문서들 읽기
        const refundStudentDocs = await Promise.all(
          refundedBidders.map(bidder => 
            transaction.get(doc(db, `schools/${schoolId}/classes/${classId}/students`, bidder.studentId))
          )
        );
        
        // 경매 상태를 완료로 변경
        transaction.update(auctionRef, {
          status: 'completed',
          isAssigned: true,
          winner: {
            studentId: auction.currentBidder,
            studentName: auction.currentBidderName,
            bidAmount: auction.currentBid
          },
          bidders: selectedBidders,
          participants: selectedBidders.map(bidder => bidder.studentId),
          updatedAt: new Date()
        });

        // 선별되지 않은 입찰자들에게 포인트 환불
        refundStudentDocs.forEach((studentDoc, index) => {
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            const currentPoints = studentData.points || 0;
            const bidder = refundedBidders[index];
            
            // 입찰 금액만큼 환불
            transaction.update(studentDoc.ref, {
              points: currentPoints + bidder.bidAmount
            });
          }
        });

        // 여기에 실제 역할 할당 로직을 추가할 수 있습니다
        // 예: 학생에게 역할 부여 등
      });

      toast.success(`${auction.currentBidderName}에게 ${auction.roleName} 역할이 할당되었습니다.`);
    } catch (err) {
      console.error('Error assigning role result:', err);
      setError('역할 할당 중 오류가 발생했습니다.');
    }
  };

    // 경매 시작
  const handleStartAuction = async (role: Role) => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      // role.id가 없으면 오류 처리
      if (!role.id) {
        console.error('Role id is missing:', role);
        setError('역할 정보가 올바르지 않습니다. 페이지를 새로고침해주세요.');
        return;
      }

      const auctionId = `auction_${role.id}_${Date.now()}`;
      const startTime = new Date();
      const endTime = new Date(startTime.getTime() + auctionSettings.auctionDuration * 60 * 1000);

      const auctionData = {
        id: auctionId,
        roleId: role.id,
        roleName: role.name,
        roleDescription: role.description,
        currentBid: role.minBidAmount,
        currentBidder: null,
        currentBidderName: null,
        minBid: role.minBidAmount,
        startTime: startTime,
        endTime: endTime,
        status: 'active' as const,
        bidders: [],
        maxRoleHolders: role.maxRoleHolders,
        participants: [],
        isAssigned: false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(doc(db, `schools/${schoolId}/classes/${classId}/roleAuctions`, auctionId), auctionData);
      toast.success(`${role.name} 역할 경매가 시작되었습니다.`);
    } catch (err) {
      console.error('Error starting auction:', err);
      setError('경매 시작 중 오류가 발생했습니다.');
    }
  };

  // 경매 취소
  const handleCancelAuction = async (auction: RoleAuction) => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      // 트랜잭션으로 경매 취소 및 포인트 환불 처리
      await runTransaction(db, async (transaction) => {
        const auctionRef = doc(db, `schools/${schoolId}/classes/${classId}/roleAuctions`, auction.id);
        
        // 먼저 모든 학생 문서 읽기
        const studentDocs = await Promise.all(
          auction.bidders.map(bidder => 
            transaction.get(doc(db, `schools/${schoolId}/classes/${classId}/students`, bidder.studentId))
          )
        );
        
        // 경매 상태를 취소로 변경
        transaction.update(auctionRef, {
          status: 'cancelled',
          updatedAt: new Date()
        });

        // 모든 입찰자에게 포인트 환불
        studentDocs.forEach((studentDoc, index) => {
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            const currentPoints = studentData.points || 0;
            const bidder = auction.bidders[index];
            
            // 입찰 금액만큼 환불
            transaction.update(studentDoc.ref, {
              points: currentPoints + bidder.bidAmount
            });
          }
        });
      });

      toast.success('경매가 취소되었고 모든 입찰자의 포인트가 환불되었습니다.');
    } catch (err) {
      console.error('Error cancelling auction:', err);
      setError('경매 취소 중 오류가 발생했습니다.');
    }
  };





  // 경매 설정 저장
  const handleSaveSettings = async () => {
    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      const settingsRef = doc(db, `schools/${schoolId}/classes/${classId}/roleAuctionSettings`, 'settings');
      await setDoc(settingsRef, auctionSettings);
      
      setSettingsDialogOpen(false);
      toast.success('경매 설정이 저장되었습니다.');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('설정 저장 중 오류가 발생했습니다.');
    }
  };



  // 컨텍스트 메뉴 핸들러
  const handleContextMenu = (event: React.MouseEvent, roleId: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      roleId
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // 정렬 변경 핸들러
  const handleSortChange = (newSortBy: 'name' | 'createdAt') => {
    if (sortBy === newSortBy) {
      // 같은 기준이면 순서만 변경
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // 다른 기준이면 기본값으로 설정
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  // 역할 편집 다이얼로그 열기
  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setNewRole({
      name: role.name,
      description: role.description,
      salary: role.salary,
      salaryType: role.salaryType,
      salaryPeriod: role.salaryPeriod,
      maxCount: role.maxCount,
      minBidAmount: role.minBidAmount,
      maxRoleHolders: role.maxRoleHolders,
      isPointManager: role.isPointManager,
      pointAutoPay: role.pointAutoPay,
      isAuctionable: role.isAuctionable || true
    });
    setRoleDialogOpen(true);
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: 0,
      position: 'relative', 
      background: 'transparent', 
      overflow: 'hidden',
      overflowX: 'hidden',
      paddingLeft: '0',
      paddingRight: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '32px'
    }}>
      {error && (
        <div style={{ 
          marginBottom: '16px', 
          position: 'relative', 
          top: 'auto', 
          left: 0, 
          right: 0, 
          zIndex: 1000, 
          padding: '12px', 
          background: '#fee', 
          color: '#c33', 
          borderRadius: '4px', 
          border: '1px solid #fcc',
          marginTop: '0'
        }}>
          {error}
          <CloseButton
            size="small"
            ariaLabel="오류 메시지 닫기"
            onClick={() => setError(null)}
            style={{ float: 'right', color: 'inherit' }}
          />
        </div>
      )}

      {success && (
        <div style={{ 
          marginBottom: '16px', 
          position: 'relative', 
          top: 'auto', 
          left: 0, 
          right: 0, 
          zIndex: 1000, 
          padding: '12px', 
          background: '#d4edda', 
          color: '#155724', 
          borderRadius: '4px', 
          border: '1px solid #c3e6cb',
          marginTop: '0'
        }}>
          {success}
          <CloseButton
            size="small"
            ariaLabel="안내 메시지 닫기"
            onClick={() => setSuccess(null)}
            style={{ float: 'right', color: 'inherit' }}
          />
        </div>
      )}

      {/* 페이지 헤더 - 스크롤 영역 밖 */}
      <div style={{
        width: '1280px',
        maxWidth: '100%',
        margin: '0 auto',
        flexShrink: 0,
        paddingTop: '0',
        paddingBottom: '0',
        paddingLeft: isMobile ? '20px' : '0',
        paddingRight: isMobile ? '20px' : '0'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: '16px',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <h1 style={{ 
            color: '#161616', 
            fontSize: isMobile ? '24px' : '32px', 
            fontFamily: 'pretendard', 
            fontWeight: '600', 
            lineHeight: isMobile ? '36px' : '48px',
            margin: 0
          }}>
            역할 경매 관리
          </h1>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px',
            alignItems: 'center'
          }}>
            <Button
              variant="primary"
              leftIcon={<span>+</span>}
              iconSize={14}
              onClick={() => {
                setEditingRole(null);
                setNewRole({
                  name: '',
                  description: '',
                  salary: 0,
                  salaryType: 'point',
                  salaryPeriod: 'monthly',
                  maxCount: 1,
                  minBidAmount: 0,
                  maxRoleHolders: 1,
                  isPointManager: false,
                  pointAutoPay: false,
                  isAuctionable: true
                });
                setRoleDialogOpen(true);
              }}
            >
              새 역할 추가
            </Button>
            <Button variant="line" onClick={() => setSettingsDialogOpen(true)}>
              경매 설정
            </Button>
          </div>
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div style={{
        width: '1280px',
        maxWidth: '100%',
        margin: '0 auto',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingLeft: isMobile ? '20px' : '0',
        paddingRight: isMobile ? '20px' : '0',
        display: 'flex',
        flexDirection: 'column',
        gap: '28px'
      }}>

        {/* 경매 활성화 스위치 */}
        <div style={{ 
          paddingLeft: '28px', 
          paddingRight: '28px', 
          paddingTop: '16px', 
          paddingBottom: '16px', 
          background: 'white', 
          borderRadius: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <div>
            <h2 style={{ 
              color: '#161616', 
              fontSize: '18px', 
              fontFamily: 'pretendard', 
              fontWeight: '600', 
              lineHeight: '27px',
              margin: '0 0 8px 0'
            }}>
              역할 경매 시스템
            </h2>
            <p style={{ 
              color: '#4C4C4C', 
              fontSize: '14px', 
              fontFamily: 'pretendard', 
              fontWeight: '400', 
              lineHeight: '21px',
              margin: 0
            }}>
              경매가 활성화되면 학생들이 역할에 입찰할 수 있습니다.
            </p>
          </div>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={auctionSettings.enabled}
              onChange={async (e) => {
                const newEnabled = e.target.checked;
                setAuctionSettings(prev => ({ ...prev, enabled: newEnabled }));
                
                // 즉시 설정 저장
                if (currentUser?.classId && currentUser?.schoolId) {
                  const settingsRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/roleAuctionSettings`, 'settings');
                  await setDoc(settingsRef, { ...auctionSettings, enabled: newEnabled });
                  
                  // 경매가 활성화되면 모든 경매 가능한 역할에 대해 경매 시작
                  if (newEnabled) {
                    const auctionableRoles = roles.filter(role => role.isAuctionable);
                    for (const role of auctionableRoles) {
                      await handleStartAuction(role);
                    }
                    if (auctionableRoles.length > 0) {
                      toast.success(`${auctionableRoles.length}개의 역할에 대해 경매가 자동으로 시작되었습니다.`);
                    }
                  } else {
                    // 경매가 비활성화되면 모든 활성 경매 취소
                    for (const auction of activeAuctions) {
                      await handleCancelAuction(auction);
                    }
                    if (activeAuctions.length > 0) {
                      toast.info('모든 활성 경매가 취소되었습니다.');
                    }
                  }
                }
              }}
              style={{
                width: '44px',
                height: '24px',
                cursor: 'pointer'
              }}
            />
            <span style={{ 
              color: '#161616', 
              fontSize: '14px', 
              fontFamily: 'pretendard', 
              fontWeight: '500', 
              lineHeight: '21px' 
            }}>
              {auctionSettings.enabled ? "경매 활성화됨" : "경매 비활성화됨"}
            </span>
          </label>
        </div>

        {/* 역할 목록 */}
        <div style={{ 
          paddingLeft: '28px', 
          paddingRight: '28px', 
          paddingTop: '24px', 
          paddingBottom: '24px', 
          background: 'white', 
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: '16px',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            <h2 style={{ 
              color: '#161616', 
              fontSize: '20px', 
              fontFamily: 'pretendard', 
              fontWeight: '600', 
              lineHeight: '30px',
              margin: 0
            }}>
              역할 목록 ({roles.length})
            </h2>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{ 
                paddingLeft: '12px', 
                paddingRight: '12px', 
                paddingTop: '6px', 
                paddingBottom: '6px', 
                background: 'white', 
                borderRadius: '8px',
                border: '1px solid #4CAF50'
              }}>
                <span style={{ 
                  color: '#4CAF50', 
                  fontSize: '12px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '500', 
                  lineHeight: '18px' 
                }}>
                  활성 경매: {activeAuctions.length}개
                </span>
              </div>
              <div style={{ 
                paddingLeft: '12px', 
                paddingRight: '12px', 
                paddingTop: '6px', 
                paddingBottom: '6px', 
                background: 'white', 
                borderRadius: '8px',
                border: '1px solid #FF6F0F'
              }}>
                <span style={{ 
                  color: '#FF6F0F', 
                  fontSize: '12px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '500', 
                  lineHeight: '18px' 
                }}>
                  완료된 경매: {completedAuctions.length}개
                </span>
              </div>
              <div style={{ 
                paddingLeft: '12px', 
                paddingRight: '12px', 
                paddingTop: '6px', 
                paddingBottom: '6px', 
                background: '#293044', 
                borderRadius: '8px',
                border: 'none'
              }}>
                <span style={{ 
                  color: 'white', 
                  fontSize: '12px', 
                  fontFamily: 'pretendard', 
                  fontWeight: '600', 
                  lineHeight: '18px' 
                }}>
                  총 동시 역할자 수: {roles.reduce((sum, role) => sum + role.maxRoleHolders, 0)}명
                </span>
              </div>
            </div>
          </div>
          
          {/* 정렬 옵션 */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            flexWrap: 'wrap'
          }}>
            <Button
              size="small"
              variant={sortBy === 'name' ? 'submit' : 'line'}
              onClick={() => handleSortChange('name')}
            >
              {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')} 이름순
            </Button>
            <Button
              size="small"
              variant={sortBy === 'createdAt' ? 'submit' : 'line'}
              onClick={() => handleSortChange('createdAt')}
            >
              {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')} 등록순
            </Button>
          </div>
          {roles.length === 0 ? (
            <div style={{ 
              padding: '24px',
              textAlign: 'center'
            }}>
              <span style={{ 
                color: '#989898', 
                fontSize: '16px', 
                fontFamily: 'pretendard', 
                fontWeight: '400', 
                lineHeight: '24px' 
              }}>
                경매할 역할이 없습니다. 새 역할을 추가해주세요.
              </span>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '16px' 
            }}>
              {sortedRoles.map((role) => (
                <div
                  key={role.id}
                  style={{
                    padding: '20px',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: '16px',
                    position: 'relative',
                    border: '1px solid #F0F0F0',
                    borderRadius: '16px',
                    background: 'white',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = '#FF6F0F';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#F0F0F0';
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px', 
                    flex: 1 
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{ 
                        color: '#161616', 
                        fontSize: '18px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '600', 
                        lineHeight: '27px',
                        margin: 0
                      }}>
                        {role.name}
                      </h3>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        alignItems: 'center' 
                      }}>
                        {role.isPointManager && (
                          <div style={{ 
                            paddingLeft: '8px', 
                            paddingRight: '8px', 
                            paddingTop: '4px', 
                            paddingBottom: '4px', 
                            background: '#FF6F0F', 
                            borderRadius: '6px' 
                          }}>
                            <span style={{ 
                              color: 'white', 
                              fontSize: '10px', 
                              fontFamily: 'pretendard', 
                              fontWeight: '600', 
                              lineHeight: '15px' 
                            }}>
                              포인트 관리자
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p style={{ 
                      color: '#4C4C4C', 
                      fontSize: '14px', 
                      fontFamily: 'pretendard', 
                      fontWeight: '400', 
                      lineHeight: '21px',
                      margin: 0
                    }}>
                      {role.description}
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      gap: '24px', 
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ 
                        color: '#FF6F0F', 
                        fontSize: '16px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '500', 
                        lineHeight: '24px' 
                      }}>
                        {role.salaryType === 'point' ? `${role.salary} 포인트` : `${role.salary} 도장`}
                      </span>
                      <span style={{ 
                        color: '#989898', 
                        fontSize: '14px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '400', 
                        lineHeight: '21px' 
                      }}>
                        {role.salaryPeriod === 'daily' ? '일급' : role.salaryPeriod === 'weekly' ? '주급' : '월급'}
                      </span>
                      <span style={{ 
                        color: '#989898', 
                        fontSize: '14px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '400', 
                        lineHeight: '21px' 
                      }}>
                        최소 입찰가: {role.minBidAmount} 포인트
                      </span>
                      <span style={{ 
                        color: '#989898', 
                        fontSize: '14px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '400', 
                        lineHeight: '21px' 
                      }}>
                        동시 역할자: {role.maxRoleHolders}명
                      </span>
                    </div>
                  
                    {/* 현재 입찰자 정보 */}
                    {(() => {
                      const activeAuction = activeAuctions.find(auction => auction.roleId === role.id);
                      if (activeAuction) {
                        return (
                          <div style={{ 
                            marginTop: '12px', 
                            padding: '12px', 
                            background: '#E8F5E9', 
                            borderRadius: '8px' 
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start',
                              gap: '16px',
                              flexWrap: isMobile ? 'wrap' : 'nowrap'
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  color: '#2E7D32', 
                                  fontSize: '14px', 
                                  fontFamily: 'pretendard', 
                                  fontWeight: '600', 
                                  lineHeight: '21px',
                                  marginBottom: '8px'
                                }}>
                                  🏆 현재 입찰 현황
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  gap: '16px', 
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  marginBottom: '12px'
                                }}>
                                  <span style={{ 
                                    color: '#2E7D32', 
                                    fontSize: '14px', 
                                    fontFamily: 'pretendard', 
                                    fontWeight: '400', 
                                    lineHeight: '21px' 
                                  }}>
                                    최고 입찰자: {
                                      auctionSettings.isAnonymous 
                                        ? '익명 입찰자' 
                                        : (activeAuction.currentBidderName || '없음')
                                    }
                                  </span>
                                  <span style={{ 
                                    color: '#2E7D32', 
                                    fontSize: '14px', 
                                    fontFamily: 'pretendard', 
                                    fontWeight: '400', 
                                    lineHeight: '21px' 
                                  }}>
                                    현재 입찰가: {activeAuction.currentBid} 포인트
                                  </span>
                                  <span style={{ 
                                    color: '#2E7D32', 
                                    fontSize: '14px', 
                                    fontFamily: 'pretendard', 
                                    fontWeight: '400', 
                                    lineHeight: '21px' 
                                  }}>
                                    입찰자 수: {activeAuction.bidders.length}명
                                  </span>
                                </div>
                                {activeAuction.bidders.length > 0 && (
                                  <div style={{ marginTop: '12px' }}>
                                    <div style={{ 
                                      color: '#2E7D32', 
                                      fontSize: '12px', 
                                      fontFamily: 'pretendard', 
                                      fontWeight: '600', 
                                      lineHeight: '18px',
                                      marginBottom: '8px'
                                    }}>
                                      입찰자 목록:
                                    </div>
                                    <div style={{ 
                                      display: 'flex', 
                                      gap: '8px', 
                                      flexWrap: 'wrap' 
                                    }}>
                                      {activeAuction.bidders.map((bidder, index) => (
                                        <div
                                          key={bidder.studentId}
                                          style={{
                                            paddingLeft: '8px',
                                            paddingRight: '8px',
                                            paddingTop: '4px',
                                            paddingBottom: '4px',
                                            background: bidder.studentId === activeAuction.currentBidder ? '#4CAF50' : 'white',
                                            borderRadius: '6px',
                                            border: bidder.studentId === activeAuction.currentBidder ? 'none' : '1px solid #E0E0E0'
                                          }}
                                        >
                                          <span style={{ 
                                            color: bidder.studentId === activeAuction.currentBidder ? 'white' : '#161616', 
                                            fontSize: '10px', 
                                            fontFamily: 'pretendard', 
                                            fontWeight: '500', 
                                            lineHeight: '15px' 
                                          }}>
                                            {auctionSettings.isAnonymous 
                                              ? `익명 입찰자 ${index + 1} (${bidder.bidAmount}포인트)`
                                              : `${bidder.studentName} (${bidder.bidAmount}포인트)`
                                            }
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                flexDirection: 'column' 
                              }}>
                                <button
                                  onClick={() => handleAssignRoleResult(activeAuction)}
                                  disabled={!activeAuction.currentBidder}
                                  style={{
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                    paddingLeft: '12px',
                                    paddingRight: '12px',
                                    background: !activeAuction.currentBidder ? '#E9E9E9' : '#4CAF50',
                                    borderRadius: '8px',
                                    border: 'none',
                                    color: !activeAuction.currentBidder ? '#989898' : 'white',
                                    fontSize: '12px',
                                    fontFamily: 'pretendard',
                                    fontWeight: '600',
                                    lineHeight: '18px',
                                    cursor: !activeAuction.currentBidder ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  결과 반영
                                </button>
                                <Button size="small" variant="dangerOutline" onClick={() => handleCancelAuction(activeAuction)}>
                                  경매 취소
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      } else if (role.isAuctionable && auctionSettings.enabled) {
                        return (
                          <div style={{ 
                            marginTop: '12px', 
                            padding: '12px', 
                            background: '#FFF3E0', 
                            borderRadius: '8px' 
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              gap: '16px',
                              flexWrap: 'wrap'
                            }}>
                              <div>
                                <div style={{ 
                                  color: '#E65100', 
                                  fontSize: '14px', 
                                  fontFamily: 'pretendard', 
                                  fontWeight: '600', 
                                  lineHeight: '21px',
                                  marginBottom: '4px'
                                }}>
                                  ⏳ 경매 대기 중
                                </div>
                                <div style={{ 
                                  color: '#E65100', 
                                  fontSize: '12px', 
                                  fontFamily: 'pretendard', 
                                  fontWeight: '400', 
                                  lineHeight: '18px' 
                                }}>
                                  아직 경매가 시작되지 않았습니다.
                                </div>
                              </div>
                              <Button size="small" variant="primary" onClick={() => handleStartAuction(role)}>
                                경매 시작
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={role.isAuctionable}
                        onChange={() => handleToggleAuctionable(role)}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{ 
                        color: '#161616', 
                        fontSize: '12px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '500', 
                        lineHeight: '18px' 
                      }}>
                        {role.isAuctionable ? "경매가능" : "경매불가"}
                      </span>
                    </label>
                    <IconButton
                      ariaLabel="역할 수정"
                      icon={<span style={{ fontSize: '18px', lineHeight: 1 }}>✏️</span>}
                      onClick={() => handleEditRole(role)}
                    />
                    <IconButton
                      ariaLabel="역할 삭제"
                      icon={<span style={{ fontSize: '18px', lineHeight: 1 }}>🗑️</span>}
                      onClick={() => role.docId && handleDeleteRole(role.docId)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 완료된 경매 목록 */}
        {completedAuctions.length > 0 && (
          <div style={{ 
            paddingLeft: '28px', 
            paddingRight: '28px', 
            paddingTop: '24px', 
            paddingBottom: '24px', 
            background: 'white', 
            borderRadius: '16px',
            marginTop: '28px'
          }}>
            <h2 style={{ 
              color: '#161616', 
              fontSize: '20px', 
              fontFamily: 'pretendard', 
              fontWeight: '600', 
              lineHeight: '30px',
              margin: '0 0 20px 0'
            }}>
              완료된 경매 ({completedAuctions.length})
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', 
              gap: '16px' 
            }}>
              {completedAuctions.slice(0, 5).map((auction) => (
                <div
                  key={auction.id}
                  style={{
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '16px',
                    background: 'white'
                  }}
                >
                  <h3 style={{ 
                    color: '#FF6F0F', 
                    fontSize: '18px', 
                    fontFamily: 'pretendard', 
                    fontWeight: '600', 
                    lineHeight: '27px',
                    margin: 0
                  }}>
                    {auction.roleName}
                  </h3>
                  {auction.winner ? (
                    <>
                      <div style={{ 
                        color: '#4CAF50', 
                        fontSize: '14px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '500', 
                        lineHeight: '21px' 
                      }}>
                        🏆 우승자: {auction.winner.studentName}
                      </div>
                      <div style={{ 
                        color: '#161616', 
                        fontSize: '14px', 
                        fontFamily: 'pretendard', 
                        fontWeight: '400', 
                        lineHeight: '21px' 
                      }}>
                        최종 입찰가: {auction.winner.bidAmount} 포인트
                      </div>
                    </>
                  ) : (
                    <div style={{ 
                      color: '#989898', 
                      fontSize: '14px', 
                      fontFamily: 'pretendard', 
                      fontWeight: '400', 
                      lineHeight: '21px' 
                    }}>
                      입찰자가 없어 취소됨
                    </div>
                  )}
                  <div style={{ 
                    color: '#989898', 
                    fontSize: '12px', 
                    fontFamily: 'pretendard', 
                    fontWeight: '400', 
                    lineHeight: '18px' 
                  }}>
                    {auction.endTime.toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 역할 생성/수정 다이얼로그 */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        title={editingRole ? '역할 수정' : '새 역할 추가'}
        width="90%"
        maxWidth="600px"
        zIndex={1000}
        actions={
          <>
            <Button variant="line" onClick={() => setRoleDialogOpen(false)}>
              취소
            </Button>
            <Button
              variant="primary"
              disabled={!newRole.name.trim()}
              onClick={editingRole ? handleUpdateRole : handleCreateRole}
            >
              {editingRole ? '수정' : '생성'}
            </Button>
          </>
        }
      >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  역할 이름 *
                </label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    width: '100%',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    color: '#161616',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  역할 설명
                </label>
                <textarea
                  value={newRole.description}
                  onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    color: '#161616',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ 
                    display: 'block',
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    급여
                  </label>
                  <input
                    type="number"
                    value={newRole.salary}
                    onChange={(e) => setNewRole(prev => ({ ...prev, salary: Number(e.target.value) }))}
                    style={{
                      width: '100%',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      border: '1px solid #F0F0F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400',
                      lineHeight: '21px',
                      color: '#161616',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block',
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    급여 유형
                  </label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        value="point"
                        checked={newRole.salaryType === 'point'}
                        onChange={(e) => setNewRole(prev => ({ ...prev, salaryType: e.target.value as 'point' | 'stamp' }))}
                      />
                      <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>포인트</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        value="stamp"
                        checked={newRole.salaryType === 'stamp'}
                        onChange={(e) => setNewRole(prev => ({ ...prev, salaryType: e.target.value as 'point' | 'stamp' }))}
                      />
                      <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>스탬프</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  급여 지급 주기
                </label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="daily"
                      checked={newRole.salaryPeriod === 'daily'}
                      onChange={(e) => setNewRole(prev => ({ ...prev, salaryPeriod: e.target.value as 'daily' | 'weekly' | 'monthly' }))}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>일간</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="weekly"
                      checked={newRole.salaryPeriod === 'weekly'}
                      onChange={(e) => setNewRole(prev => ({ ...prev, salaryPeriod: e.target.value as 'daily' | 'weekly' | 'monthly' }))}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>주간</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="monthly"
                      checked={newRole.salaryPeriod === 'monthly'}
                      onChange={(e) => setNewRole(prev => ({ ...prev, salaryPeriod: e.target.value as 'daily' | 'weekly' | 'monthly' }))}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>월간</span>
                  </label>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ 
                    display: 'block',
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    최소 입찰가 (포인트)
                  </label>
                  <input
                    type="number"
                    value={newRole.minBidAmount}
                    onChange={(e) => setNewRole(prev => ({ ...prev, minBidAmount: Number(e.target.value) }))}
                    style={{
                      width: '100%',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      border: '1px solid #F0F0F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400',
                      lineHeight: '21px',
                      color: '#161616',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block',
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    동시 역할자 수
                  </label>
                  <input
                    type="number"
                    value={newRole.maxRoleHolders}
                    onChange={(e) => setNewRole(prev => ({ ...prev, maxRoleHolders: Number(e.target.value) }))}
                    style={{
                      width: '100%',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      border: '1px solid #F0F0F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400',
                      lineHeight: '21px',
                      color: '#161616',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ 
                    color: '#989898',
                    fontSize: '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '18px',
                    marginTop: '4px'
                  }}>
                    이 역할을 동시에 맡을 수 있는 학생 수
                  </div>
                </div>
              </div>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newRole.isPointManager}
                  onChange={(e) => setNewRole(prev => ({ ...prev, isPointManager: e.target.checked }))}
                />
                <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>포인트 관리자 권한</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newRole.pointAutoPay}
                  onChange={(e) => setNewRole(prev => ({ ...prev, pointAutoPay: e.target.checked }))}
                />
                <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>자동 급여 지급</span>
              </label>
            </div>
      </Dialog>

      

      {/* 경매 설정 다이얼로그 */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        title="경매 설정"
        width="90%"
        maxWidth="500px"
        zIndex={1000}
        actions={
          <>
            <Button variant="line" onClick={() => setSettingsDialogOpen(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSaveSettings}>
              저장
            </Button>
          </>
        }
      >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  기본 경매 시간 (분)
                </label>
                <input
                  type="number"
                  value={auctionSettings.auctionDuration}
                  onChange={(e) => setAuctionSettings(prev => ({ ...prev, auctionDuration: Number(e.target.value) }))}
                  min={1}
                  max={1440}
                  disabled={auctionSettings.endCondition !== 'time'}
                  style={{
                    width: '100%',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    color: '#161616',
                    boxSizing: 'border-box',
                    opacity: auctionSettings.endCondition !== 'time' ? 0.5 : 1
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  최소 입찰 증가액
                </label>
                <input
                  type="number"
                  value={auctionSettings.minBidIncrement}
                  onChange={(e) => setAuctionSettings(prev => ({ ...prev, minBidIncrement: Number(e.target.value) }))}
                  min={1}
                  style={{
                    width: '100%',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    color: '#161616',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ 
                  color: '#989898',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '400',
                  lineHeight: '18px',
                  marginTop: '4px'
                }}>
                  학생들이 상위 금액을 입찰하려면 이 금액만큼 더 높은 금액을 입력해야 합니다
                </div>
              </div>
             
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  경매 종료 조건
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="time"
                      checked={auctionSettings.endCondition === 'time'}
                      onChange={(e) => setAuctionSettings(prev => ({ ...prev, endCondition: e.target.value as 'time' | 'fixedTime' | 'manual' }))}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>시간 기반 종료</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="fixedTime"
                      checked={auctionSettings.endCondition === 'fixedTime'}
                      onChange={(e) => setAuctionSettings(prev => ({ ...prev, endCondition: e.target.value as 'time' | 'fixedTime' | 'manual' }))}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>지정 시간 종료</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="manual"
                      checked={auctionSettings.endCondition === 'manual'}
                      onChange={(e) => setAuctionSettings(prev => ({ ...prev, endCondition: e.target.value as 'time' | 'fixedTime' | 'manual' }))}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>수동 종료</span>
                  </label>
                </div>
              </div>

              {auctionSettings.endCondition === 'fixedTime' && (
                <div>
                  <label style={{ 
                    display: 'block',
                    color: '#161616',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    marginBottom: '8px'
                  }}>
                    종료 시간
                  </label>
                  <input
                    type="datetime-local"
                    value={auctionSettings.fixedEndTime ? new Date(auctionSettings.fixedEndTime.getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setAuctionSettings(prev => ({ 
                      ...prev, 
                      fixedEndTime: e.target.value ? new Date(e.target.value) : undefined 
                    }))}
                    style={{
                      width: '100%',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      border: '1px solid #F0F0F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400',
                      lineHeight: '21px',
                      color: '#161616',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ 
                    color: '#989898',
                    fontSize: '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '18px',
                    marginTop: '4px'
                  }}>
                    경매가 종료될 정확한 시간을 지정합니다
                  </div>
                </div>
              )}
              
              <div>
                <label style={{ 
                  display: 'block',
                  color: '#161616',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '21px',
                  marginBottom: '8px'
                }}>
                  기본 동시 역할자 수
                </label>
                <input
                  type="number"
                  value={auctionSettings.defaultMaxRoleHolders}
                  onChange={(e) => setAuctionSettings(prev => ({ ...prev, defaultMaxRoleHolders: Number(e.target.value) }))}
                  min={1}
                  style={{
                    width: '100%',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    color: '#161616',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ 
                  color: '#989898',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '400',
                  lineHeight: '18px',
                  marginTop: '4px'
                }}>
                  새 역할 생성 시 기본값
                </div>
              </div>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={auctionSettings.autoAssignOnEnd}
                  onChange={(e) => setAuctionSettings(prev => ({ ...prev, autoAssignOnEnd: e.target.checked }))}
                />
                <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>경매 종료 시 자동 역할 할당</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={auctionSettings.isAnonymous}
                  onChange={(e) => setAuctionSettings(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                />
                <span style={{ fontSize: '14px', fontFamily: 'pretendard' }}>익명 경매</span>
              </label>
              
              {auctionSettings.isAnonymous && (
                <div style={{ 
                  padding: '12px',
                  background: '#E3F2FD',
                  borderRadius: '8px',
                  border: '1px solid #90CAF9',
                  marginTop: '8px'
                }}>
                  <div style={{ 
                    color: '#1565C0',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '21px'
                  }}>
                    익명 경매에서는 입찰자 이름이 숨겨지고 입찰 금액만 표시됩니다.
                    경매 종료 후에만 실제 입찰자 정보가 공개됩니다.
                  </div>
                </div>
              )}
            </div>
      </Dialog>
    </div>
  );
};

export default RoleAuctionManagement;
